import React, { FC } from "react";
import { css, styled } from "@mui/system";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, RADII, SPACINGS } from "../../common/consts";
import { makeShouldForwardProps } from "../../common/utils/ui";

/**
 * Pill sub-navigation between the reports on the Insights page, so one
 * dashboard shows at a time. Outreach's own pill treatment: fully rounded,
 * brand text, a light brand fill when selected and a neutral fill on hover.
 */

export type ReportKey = "email" | "profile" | "phone" | "linkedin";

export const REPORTS: Array<{ key: ReportKey; label: string }> = [
  { key: "email", label: "Email" },
  { key: "profile", label: "Profile" },
  { key: "phone", label: "Phone" },
  { key: "linkedin", label: "LinkedIn" },
];

interface PillProps {
  active?: boolean;
  className?: string;
  type?: "button";
  onClick?: () => void;
  children?: React.ReactNode;
}

const shouldForwardProp = makeShouldForwardProps<PillProps>(["active"]);

/* Same type treatment as the tabs above it; selected simply keeps the hover fill. */
const Pill = styled("button", { shouldForwardProp })<PillProps>`
  height: 2.25rem;
  padding: 0 ${SPACINGS.lg};
  border-radius: ${RADII.full};
  border: none;
  background-color: transparent;
  color: var(--color-tabInactiveText, ${COLORS.tabInactiveText});
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--color-tabHoverBg, ${COLORS.tabHoverBg});
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }

  ${({ active }) =>
    active &&
    css`
      background-color: var(--color-backgroundBrandTab, ${COLORS.backgroundBrandTab});

      &:hover {
        background-color: var(--color-backgroundBrandTab, ${COLORS.backgroundBrandTab});
      }
    `}
`;

/* Indented so the first label lines up with the tab labels above. */
const Bar = styled("div")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.xs};
  flex-wrap: wrap;
  padding-left: ${SPACINGS.sm};
  /* Sits closer to the panel it controls than to the header above it. */
  margin-bottom: -${SPACINGS.sm};
`;

interface Props {
  value: ReportKey;
  onChange: (value: ReportKey) => void;
}

const ReportNav: FC<Props> = ({ value, onChange }) => (
  <Bar>
    {REPORTS.map((r) => (
      <Pill
        key={r.key}
        type="button"
        active={r.key === value}
        className={r.key === value ? "boring-report-pill boring-report-pill-active" : "boring-report-pill"}
        onClick={() => onChange(r.key)}
      >
        {r.label}
      </Pill>
    ))}
  </Bar>
);

export default ReportNav;
